export const normalizeCatalogId = value => String(value ?? '').trim();

export const cleanCatalogText = value => String(value ?? '')
    .trim()
    .replace(/\s+/g, ' ');

const CATALOG_HTML_TAG_PATTERN = /<[^>]*>/g;
const CATALOG_PUNCTUATION_PATTERN = /[^a-z0-9]+/g;
const CATALOG_ITEM_FIELDS = Array.from(
    { length: 10 },
    (_, index) => `item${index + 1}`
);

const RELEVANCE_WEIGHTS = Object.freeze({
    exactTitle: 1000000,
    titleStartsWithQuery: 500000,
    allTermsInTitle: 250000,
    titleTerm: 10000,
    categoryTerm: 2000,
    visibleItemTerm: 1000,
    descriptionTerm: 250,
});

export const normalizeCatalogSearchText = value => cleanCatalogText(value)
    .toLocaleLowerCase('es-CO')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(CATALOG_HTML_TAG_PATTERN, ' ')
    .replace(CATALOG_PUNCTUATION_PATTERN, ' ')
    .trim()
    .replace(/\s+/g, ' ');

const createCatalogSearchTerms = (value) => {
    const normalizedText = normalizeCatalogSearchText(value);

    return normalizedText
        ? Array.from(new Set(normalizedText.split(' ').filter(Boolean)))
        : [];
};

export const createCatalogCategoryNameMap = (categories = []) => {
    const categoryNames = new Map();

    const categoryList = Array.isArray(categories) ? categories : [];

    categoryList.forEach((category) => {
        const categoryId = normalizeCatalogId(category?.idCategoria);
        const categoryName = cleanCatalogText(category?.categoria);

        if (categoryId && categoryName && !categoryNames.has(categoryId)) {
            categoryNames.set(categoryId, categoryName);
        }
    });

    return categoryNames;
};

const visibleCatalogItems = (product) => {
    const itemsAreVisible = String(product?.verItems ?? '')
        .trim()
        .toLocaleLowerCase('es-CO') === 'si';

    if (!itemsAreVisible) {
        return [];
    }

    return CATALOG_ITEM_FIELDS
        .map(field => normalizeCatalogSearchText(product?.[field]))
        .filter(Boolean);
};

const getTitleBonus = (normalizedTitle, normalizedQuery, searchTerms) => {
    if (normalizedTitle === normalizedQuery) {
        return RELEVANCE_WEIGHTS.exactTitle;
    }

    if (normalizedTitle.startsWith(normalizedQuery)) {
        return RELEVANCE_WEIGHTS.titleStartsWithQuery;
    }

    if (searchTerms.every(term => normalizedTitle.includes(term))) {
        return RELEVANCE_WEIGHTS.allTermsInTitle;
    }

    return 0;
};

const getBestTermWeight = (term, searchableFields) => {
    if (searchableFields.title.includes(term)) {
        return RELEVANCE_WEIGHTS.titleTerm;
    }

    if (searchableFields.category.includes(term)) {
        return RELEVANCE_WEIGHTS.categoryTerm;
    }

    if (searchableFields.visibleItems.some(item => item.includes(term))) {
        return RELEVANCE_WEIGHTS.visibleItemTerm;
    }

    if (searchableFields.description.includes(term)) {
        return RELEVANCE_WEIGHTS.descriptionTerm;
    }

    return 0;
};

export const filterCatalogProducts = (
    products = [],
    categories = [],
    searchTerm = ''
) => {
    const productList = Array.isArray(products) ? products : [];
    const normalizedQuery = normalizeCatalogSearchText(searchTerm);

    if (!normalizedQuery) {
        return productList.slice();
    }

    const categoryNames = createCatalogCategoryNameMap(categories);
    const searchTerms = createCatalogSearchTerms(searchTerm);

    return productList
        .map((product, originalIndex) => {
            const categoryName = categoryNames.get(
                normalizeCatalogId(product?.idCategoria)
            );
            const searchableFields = {
                title: normalizeCatalogSearchText(product?.titulo),
                category: normalizeCatalogSearchText(categoryName),
                description: normalizeCatalogSearchText(product?.descripcion),
                visibleItems: visibleCatalogItems(product),
            };
            const termWeights = searchTerms.map(
                term => getBestTermWeight(term, searchableFields)
            );

            if (termWeights.some(weight => weight === 0)) {
                return null;
            }

            const score = getTitleBonus(
                searchableFields.title,
                normalizedQuery,
                searchTerms
            ) + termWeights.reduce((total, weight) => total + weight, 0);

            return {
                originalIndex,
                product,
                score,
            };
        })
        .filter(result => result !== null)
        .sort((firstResult, secondResult) => (
            secondResult.score - firstResult.score
            || firstResult.originalIndex - secondResult.originalIndex
        ))
        .map(result => result.product);
};
